import { useCallback, useState } from 'react';
import { ErrorCode, FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { MIN_RESOLUTION } from './constants';
import { errorMessage } from './helpers';
import { ImageErrorCode } from './types';

type PropsType = {
  onSuccess: (imageFile: File) => void;
};

const useImageValidation = ({ onSuccess }: PropsType) => {
  const { t } = useTranslation();

  const [validationError, setValidationError] = useState<Error | null>(null);

  const validateImage = useCallback(
    (image: HTMLImageElement) => {
      if (image.height !== image.width) {
        throw new Error(t(errorMessage(ImageErrorCode.ImageNotSquare)));
      }
      if (image.width < MIN_RESOLUTION || image.height < MIN_RESOLUTION) {
        throw new Error(
          t(errorMessage(ImageErrorCode.ImageResolutionTooSmall))
        );
      }
    },
    [t]
  );

  const loadImage = useCallback(
    (imageFile: File) => {
      const imageSrc = URL.createObjectURL(imageFile);
      const image = new Image();
      image.addEventListener('load', () => {
        URL.revokeObjectURL(imageSrc);
        try {
          validateImage(image);
        } catch (error) {
          setValidationError(error as Error);
          return;
        }
        setValidationError(null);
        onSuccess(imageFile);
      });
      image.src = imageSrc;
    },
    [onSuccess, validateImage]
  );

  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) {
        return;
      }
      if (acceptedFiles.length > 1) {
        setValidationError(new Error(t(errorMessage(ErrorCode.TooManyFiles))));
        return;
      }

      const imageFile = acceptedFiles[0];
      loadImage(imageFile);
    },
    [loadImage, t]
  );

  const onDropRejected = useCallback(
    (rejectedFiles: FileRejection[]) => {
      if (!rejectedFiles.length) {
        return;
      }
      if (rejectedFiles.length > 1) {
        setValidationError(new Error(t(errorMessage(ErrorCode.TooManyFiles))));
        return;
      }
      const { errors } = rejectedFiles[0];
      if (!errors.length) {
        return;
      }

      const message = errorMessage(errors[0].code);
      setValidationError(new Error(t(message)));
    },
    [t, setValidationError]
  );

  return {
    validationError,
    onDropAccepted,
    onDropRejected,
  };
};

export default useImageValidation;
